import {
  Form,
  Input,
  InputRef,
  Modal,
  Button,
  Select,
  FormInstance,
  Tooltip,
  Alert,
} from 'antd';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classes from './NewRoomModal.module.scss';
import RowLoader from 'components/Sceletons/RowLoader';
import api from 'api';
import { TFunction } from 'i18next';
import { QuoteData } from 'components/TariffsComponent/tariffsData';
import axios from 'axios';
import cn from 'classnames';
import { getTheme } from 'helpers/getTheme';
import { useAppSelector } from 'store/hook';

type GroupedTariff = {
  tariffId: number;
  tariffName: string;
  roomsCount: number;
  roomsLimit: number;
  membersLimit: number;
  storageLimit: number;
  canCreateRoom: boolean;
  quoteName: string;
};

type CreateRoomModalProps = {
  isOpen: boolean;
  onCancel: () => void;
  onSubmit: (values: any, quoteName: string, quotes: QuoteData[]) => void;
  isCreatingRoom: boolean;
  form: FormInstance;
  nameInput: React.RefObject<InputRef>;
  fields: { name: string; description: string };
  onInputsChange: (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  onInputsPaste: (
    event: React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  onNavigateToTariffs: () => void;
  billingActive: boolean;
};

const getTariffLabel = (tariff: GroupedTariff, t: TFunction) => {
  return (
    <>
      {tariff.quoteName && (
        <>
          <Tooltip title={tariff.quoteName} placement='topLeft'>
            <span className={classes.quoteName}>[{tariff.quoteName}]</span>
          </Tooltip>
          :{' '}
        </>
      )}
      {t(`Tariffs.tariff_name.${tariff.tariffName}`, t('Tariffs.tariff_name.unknown'))} ({tariff.roomsCount}/
      {tariff.roomsLimit} {t('Header.createModal.roomsCount')})
    </>
  );
};

export default function CreateRoomModal({
  isOpen,
  onCancel,
  onSubmit,
  isCreatingRoom,
  form,
  nameInput,
  fields,
  onInputsChange,
  onInputsPaste,
  onNavigateToTariffs,
  billingActive
}: CreateRoomModalProps) {
  const { t } = useTranslation();
  const [quotes, setQuotes] = useState<QuoteData[]>([]);
  const [isLoadingTariffs, setIsLoadingTariffs] = useState(false);
  const [groupedTariffs, setGroupedTariffs] = useState<GroupedTariff[]>([]);
  const [selectedTariff, setSelectedTariff] =
    React.useState<GroupedTariff | null>(null);
  const [hasNoTariffs, setHasNoTariffs] = useState(false);

  const { messageApi } = useAppSelector((state) => state.windowState);
  
  useEffect(() => {
    const loadUserTariffsAndQuotes = async () => {
      setIsLoadingTariffs(true);
      setHasNoTariffs(false);
      try {
        const { data } = await api.getTariffDetails();

        const grouped: GroupedTariff[] = data.map((tariff: any) => ({
          tariffId: tariff.id,
          tariffName: tariff.name,
          roomsCount: tariff.room_count || 0,
          roomsLimit: tariff.base_constraints.rooms || 1,
          membersLimit: tariff.base_constraints.members,
          storageLimit: tariff.base_constraints.storage,
          canCreateRoom:
            (tariff.room_count || 0) < (tariff.base_constraints.rooms || 1),
          quoteName: tariff.quote_name,
        }));

        setGroupedTariffs(grouped);

        const availableTariff = grouped.find((t) => t.canCreateRoom);
        if (availableTariff) {
          setSelectedTariff(availableTariff);
        }

        if (data.length > 0) {
          const { data: quotesData } = await api.getQuotes();
          setQuotes(quotesData);
        }
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          setHasNoTariffs(true);
        } else {
          messageApi?.error(t('Header.error.loadTariffs'));
        }
      } finally {
        setIsLoadingTariffs(false);
      }
    };
    if (isOpen && billingActive) {
      loadUserTariffsAndQuotes();
    }
  }, [isOpen, billingActive, t]);

  const onTariffSelect = (quoteName: string) => {
    const tariff = groupedTariffs.find((t) => t.quoteName === quoteName);
    setSelectedTariff(tariff || null);
  };  

  return (
    <Modal
      styles={{ body: { maxHeight: '85vh' } }}
      width={'550px'}
      title={t('Header.createModal.title')}
      open={isOpen}
      centered={true}
      mask={{ closable: false }}
      onCancel={onCancel}
      onOk={form.submit}
      cancelButtonProps={{ style: { display: 'none' } }}
      confirmLoading={isCreatingRoom}
      className={cn(classes.modalAddRoom, getTheme())}
      okButtonProps={{
        disabled: billingActive ? (!selectedTariff?.canCreateRoom || isLoadingTariffs || hasNoTariffs) : false,
        style: { width: '100%', margin: 0 },
      }}
      okText={t('Header.createModal.createButton')}
    >
      <div className={classes.modalWrap}>
        {billingActive && hasNoTariffs && (
          <Alert
            message={t('Header.createModal.noTariffs')}
            description={t('Header.createModal.noTariffsDescription')}
            type='warning'
            showIcon
            className={classes.alertBtn}
            action={
              <Button
                type='primary'
                onClick={onNavigateToTariffs}
              >
                {t('Header.createModal.toTariffs')}
              </Button>
            }
          />
        )}

        {billingActive && !hasNoTariffs && (
          <div className={classes.tariffsWrap}>
            <div className={classes.tariffsSelect}>
              <Select
                placeholder={t('Header.createModal.selectPreviousTariff')}
                value={selectedTariff?.quoteName}
                onChange={(quoteName: string) => onTariffSelect(quoteName)}
                className={classes.select}
                loading={isLoadingTariffs}
                options={groupedTariffs.map((tariff) => ({
                  label: getTariffLabel(tariff, t),
                  value: tariff.quoteName,
                }))}
              />
              <Button
                type='primary'
                onClick={onNavigateToTariffs}
                className={classes.btn}
              >
                {t('Header.createModal.toTariffs')}
              </Button>
            </div>

            {selectedTariff && !isLoadingTariffs && (
              <div className={classes.infoWrap}>
                <div className={classes.statistic}>
                  {t('Header.createModal.tariffStats')}:
                </div>
                <div className={classes.statList}>
                  <div>
                    {t('Header.createModal.rooms')}:{' '}
                    {isLoadingTariffs ? (
                      <RowLoader
                        height={16}
                        width={60}
                        padding={'0 0 0 0'}
                        display='inline-block'
                        verticalAlign='middle'
                      />
                    ) : selectedTariff ? (
                      `${selectedTariff.roomsCount}/${selectedTariff.roomsLimit}`
                    ) : (
                      '-/-'
                    )}
                  </div>
                  <div>
                    {t('Header.createModal.members')}:{' '}
                    {isLoadingTariffs ? (
                      <RowLoader
                        height={16}
                        width={60}
                        padding={'0 0 0 0'}
                        display='inline-block'
                        verticalAlign='middle'
                      />
                    ) : selectedTariff ? (
                      `${selectedTariff.membersLimit}`
                    ) : (
                      '-/-'
                    )}
                  </div>
                  <div>
                    {t('Header.createModal.storage')}:{' '}
                    {isLoadingTariffs ? (
                      <RowLoader
                        height={16}
                        width={80}
                        padding={'0 0 0 0'}
                        display='inline-block'
                        verticalAlign='middle'
                      />
                    ) : selectedTariff ? (
                      `${selectedTariff.storageLimit} ${t('Tariffs.gb')}`
                    ) : (
                      `- ${t('Tariffs.gb')}`
                    )}
                  </div>
                  {!isLoadingTariffs &&
                    selectedTariff &&
                    !selectedTariff.canCreateRoom && (
                      <div className={classes.noSlots}>
                        {t('Header.createModal.noRoomSlots')}
                      </div>
                    )}
                </div>
              </div>
            )}

            <div className={classes.divider}/>
          </div>
        )}

        <Form
          className={classes.form}
          layout='vertical'
          onFinish={(values) => onSubmit(values, selectedTariff?.quoteName!, quotes)}
          form={form}
        >
          <Form.Item
            className={classes.formInput}
            label={t('Header.createModal.nameLabel')}
            name='name'
            rules={[
              {
                required: true,
                whitespace: true,
                message: t('Header.createModal.roomNameValidate'),
              },
              {
                pattern: /^(?!.*\s{2,}).*$/,
                message: t('Header.createModal.multipleSpacesError'),
              },
            ]}
          >
            <Input
              name='name'
              ref={nameInput}
              count={{
                show: true,
                max: 80,
              }}
              value={fields.name}
              onChange={onInputsChange}
              onPaste={onInputsPaste}
              maxLength={80}
              placeholder={t('Header.createModal.namepPaceholder')}
            />
          </Form.Item>

          <Form.Item
            label={t('Settings.room.labels.description')}
            name='description'
          >
            <Input.TextArea
              placeholder={t('Settings.room.placeholders.description')}
              name='description'
              autoSize={{ minRows: 4, maxRows: 4 }}
              value={fields.description}
              onChange={onInputsChange}
              onPaste={onInputsPaste}
              maxLength={500}
              showCount
            />
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
}
